import { createFileRoute, useNavigate } from "@tanstack/react-router";
import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { requireAuth } from "~/lib/auth-guard";
import { orpc, client } from "~/lib/orpc-client";
import { Skeleton, useToast } from "@alchema/ui";
import { CONSENT_VERSIONS, getConsentText } from "~/lib/consent-versions";
import type { ConsentState } from "~/components/auth/ConsentCheckboxes";
import { getLocale } from "~/paraglide/runtime";
import * as m from "~/paraglide/messages";
import type { AlchemaResumeData } from "~/lib/resume/types";
import { OnboardingAtsScore } from "~/components/OnboardingAtsScore";
import { asBlogPageType } from "~/lib/analytics-events";
import { trackEvent } from "~/lib/posthog-client";
import { ResumeUploadError, runResumeUpload } from "~/lib/resume/upload-import";

export const Route = createFileRoute("/onboarding")({
  beforeLoad: async () => { await requireAuth(); },
  component: OnboardingPage,
  pendingComponent: OnboardingSkeleton,
});

function OnboardingSkeleton() {
  return (
    <div className="flex min-h-screen items-center justify-center p-4" style={{ backgroundColor: "var(--bg)" }}>
      <div className="w-full max-w-lg" aria-busy="true" role="status" aria-label="Loading onboarding">
        {/* Progress bar skeleton */}
        <div className="space-y-2">
          <div className="flex justify-between">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-8" />
          </div>
          <Skeleton className="h-1.5 w-full rounded-full" />
          <div className="flex justify-between pt-1">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex flex-col items-center gap-1">
                <Skeleton className="h-6 w-6 rounded-full" />
                <Skeleton className="h-3 w-12" />
              </div>
            ))}
          </div>
        </div>
        {/* Card skeleton */}
        <div className="mt-6 rounded-xl border p-8" style={{ borderColor: "var(--border-subtle)", background: "var(--surface)" }}>
          <div className="flex flex-col items-center gap-3">
            <Skeleton className="h-14 w-14 rounded-[13px]" />
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>
          <div className="mt-8 space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16 rounded-lg" />
            ))}
          </div>
          <Skeleton className="mt-8 h-10 w-full rounded-[10px]" />
        </div>
      </div>
    </div>
  );
}

function getStepLabels() {
  return [m.onboarding_step_welcome(), m.onboarding_step_resume(), m.onboarding_step_ready()];
}

function getStepTimes() {
  return [m.onboarding_step_time_15(), m.onboarding_step_time_30(), m.onboarding_step_time_10()];
}

function ProgressBar({ current, total }: { current: number; total: number }) {
  const progress = Math.round(((current - 1) / (total - 1)) * 100);
  const displayStep = current;
  const labels = getStepLabels();
  const times = getStepTimes();

  return (
    <div className="w-full space-y-2">
      <div className="flex items-center justify-between text-xs" style={{ color: "var(--text-muted)" }}>
        <span className="font-medium">
          {m.onboarding_progress({ current: String(current), total: String(total), time: times[current - 1] })}
        </span>
        <span className="mono tabular">{progress}%</span>
      </div>

      <div className="h-1.5 w-full overflow-hidden rounded-full" style={{ backgroundColor: "var(--surface-inset)" }}>
        <div
          className="h-full rounded-full"
          style={{
            width: `${progress}%`,
            backgroundColor: "var(--accent)",
            transition: "width 500ms var(--ease-out)",
          }}
        />
      </div>

      <div className="flex justify-between pt-1">
        {labels.map((label, i) => {
          const stepNum = i + 1;
          const isDone = displayStep > stepNum;
          const isCurrent = displayStep === stepNum;
          return (
            <div key={label} className="flex flex-col items-center gap-1">
              <div
                className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium mono"
                style={{
                  backgroundColor: isDone || isCurrent ? "var(--accent)" : "var(--surface-inset)",
                  color: isDone || isCurrent ? "var(--accent-contrast)" : "var(--text-muted)",
                  transition: "all 300ms var(--ease-out)",
                }}
              >
                {isDone ? (
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : stepNum}
              </div>
              <span
                className="text-xs font-medium truncate max-w-[4rem] sm:max-w-none text-center"
                style={{ color: isCurrent ? "var(--text)" : "var(--text-muted)", transition: "color var(--duration-fast) var(--ease-out)" }}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OnboardingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: status } = useQuery({
    ...orpc.user.getOnboardingStatus.queryOptions(),
    retry: 0,
  });

  React.useEffect(() => {
    if (status?.complete) {
      navigate({ to: "/dashboard" });
    }
  }, [status, navigate]);

  // Affonso affiliate signup tracking for OAuth users (email signup tracked in signup.tsx)
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem("alchema_affonso_tracked")) return;
    sessionStorage.setItem("alchema_affonso_tracked", "1");
    if ((window as any).Affonso?.signup) {
      // For OAuth users we don't have email readily — Affonso will match via cookie
      (window as any).Affonso.signup({});
    }
  }, []);

  // ANAL-02: Fire signup_from_blog for OAuth signups (email path fires in signup.tsx)
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const blogRef = sessionStorage.getItem("blog_referrer");
      if (blogRef) {
        const { pageType: sourcePageType } = JSON.parse(blogRef) as { pageType?: unknown };
        const safePageType = asBlogPageType(sourcePageType);
        if (safePageType) trackEvent("signup_from_blog", { source_pageType: safePageType });
        sessionStorage.removeItem("blog_referrer");
      }
    } catch {
      // Malformed JSON or sessionStorage unavailable
    }
  }, []);

  // Persist OAuth consent from sessionStorage (set by signup page before Google redirect)
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const stored = sessionStorage.getItem("alchema_consent_state");
      if (!stored) return;
      const { consents: storedConsents, timestamp } = JSON.parse(stored) as {
        consents: ConsentState;
        timestamp: number;
      };
      // Ignore stale consent (older than 10 minutes)
      if (Date.now() - timestamp > 10 * 60 * 1000) {
        sessionStorage.removeItem("alchema_consent_state");
        return;
      }
      const locale = getLocale();
      const consentPayload = (Object.keys(storedConsents) as Array<keyof ConsentState>)
        .filter((key) => storedConsents[key] || key === "terms_privacy" || key === "ai_processing")
        .map((type) => ({
          type,
          granted: storedConsents[type],
          version: CONSENT_VERSIONS[type],
          text: getConsentText(type, locale),
        }));
      client.consent.record({ consents: consentPayload, source: "oauth_callback" }).catch(() => {
        console.error("[consent] Failed to persist OAuth consent");
      });
      sessionStorage.removeItem("alchema_consent_state");
    } catch {
      console.error("[consent] Failed to read OAuth consent from sessionStorage");
    }
  }, []);

  const totalSteps = 3;

  const [step, setStep] = React.useState(1);
  const [uploadedResumeName, setUploadedResumeName] = React.useState<string | null>(null);
  const [resumeCreated, setResumeCreated] = React.useState(false);
  const [needsEditor, setNeedsEditor] = React.useState(false);
  const [parsedData, setParsedData] = React.useState<AlchemaResumeData | null>(null);

  const parseUploadMutation = useMutation(orpc.resume.parseUpload.mutationOptions());
  const resumeCreateMutation = useMutation(orpc.resume.create.mutationOptions());
  const completeOnboardingMutation = useMutation(orpc.user.completeOnboarding.mutationOptions());

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    try {
      const persisted = await runResumeUpload(file, {
        parse: (input) => parseUploadMutation.mutateAsync(input),
        persist: (input) => resumeCreateMutation.mutateAsync(input),
      });
      setParsedData(persisted.data as unknown as AlchemaResumeData);
      setUploadedResumeName(persisted.data.basics.name || persisted.title);
      setResumeCreated(true);
    } catch (error) {
      const code = error instanceof ResumeUploadError ? error.code : "parse";
      toast(
        code === "type"
          ? m.onboarding_upload_error_type()
          : code === "size"
            ? m.onboarding_upload_error_size()
            : m.onboarding_upload_error_parse(),
        "error",
      );
    }
  }

  function handleBuildFromScratch() {
    resumeCreateMutation.mutate(
      { title: "My Resume" },
      {
        onSuccess: () => {
          setResumeCreated(true);
          setNeedsEditor(true);
        },
      },
    );
  }

  function handleComplete() {
    completeOnboardingMutation.mutate(undefined, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.user.getOnboardingStatus.key({}) });
        if (needsEditor) {
          navigate({ to: "/resumes" });
        } else {
          navigate({ to: "/dashboard" });
        }
      },
    });
  }

  function goNext() {
    setStep((s) => Math.min(s + 1, totalSteps));
  }

  function goBack() {
    setStep((s) => Math.max(s - 1, 1));
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4" style={{ backgroundColor: "var(--bg)" }}>
      <div className="w-full max-w-lg animate-fade-up">
        <ProgressBar current={step} total={3} />

        <div className="card mt-6 p-4 sm:p-8">
          {step === 1 && <StepWelcome onNext={goNext} />}
          {step === 2 && (
            <StepResume
              onUpload={handleFileUpload}
              onBuildFromScratch={handleBuildFromScratch}
              isUploading={parseUploadMutation.isPending || resumeCreateMutation.isPending}
              uploadError={parseUploadMutation.error?.message || resumeCreateMutation.error?.message}
              resumeCreated={resumeCreated}
              uploadedName={uploadedResumeName}
              parsedResumeData={parsedData}
              onNext={goNext}
              onBack={goBack}
              onSkip={goNext}
            />
          )}
          {step === 3 && (
            <StepComplete
              uploadedName={uploadedResumeName}
              onComplete={handleComplete}
              isCompleting={completeOnboardingMutation.isPending}
              onBack={goBack}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function StepWelcome({ onNext }: { onNext: () => void }) {
  return (
    <div className="text-center">
      <div
        className="mx-auto flex h-14 w-14 items-center justify-center rounded-[13px]"
        style={{ backgroundColor: "var(--accent-subtle)" }}
      >
        <svg className="h-7 w-7" style={{ color: "var(--accent)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      </div>
      <h1 className="mt-5 text-2xl font-bold" style={{ color: "var(--text)" }}>{m.onboarding_welcome_title()}</h1>
      <p className="mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>
        {m.onboarding_welcome_subtitle()}
      </p>
      <p className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>
        {m.onboarding_welcome_skip_note()}
      </p>

      <div className="mt-8 space-y-3 text-left">
        {[
          { icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z", title: m.onboarding_welcome_feature_resume_title(), desc: m.onboarding_welcome_feature_resume_desc() },
          { icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z", title: m.onboarding_welcome_feature_ats_title(), desc: m.onboarding_welcome_feature_ats_desc() },
        ].map((item) => (
          <div key={item.title} className="card card-interactive p-3 flex items-start gap-3">
            <div
              className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[10px]"
              style={{ backgroundColor: "var(--surface-raised)" }}
            >
              <svg className="h-4 w-4" style={{ color: "var(--text-muted)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium" style={{ color: "var(--text)" }}>{item.title}</p>
              <p className="text-xs" style={{ color: "var(--text-muted)" }}>{item.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <button onClick={onNext} className="btn btn-accent mt-8 w-full">
        {m.common_get_started()}
      </button>
    </div>
  );
}

function StepResume({
  onUpload, onBuildFromScratch, isUploading, uploadError, resumeCreated, uploadedName, parsedResumeData, onNext, onBack, onSkip,
}: {
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBuildFromScratch: () => void;
  isUploading: boolean;
  uploadError?: string;
  resumeCreated: boolean;
  uploadedName: string | null;
  parsedResumeData: AlchemaResumeData | null;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}) {
  return (
    <div>
      <h2 className="text-xl font-bold" style={{ color: "var(--text)" }}>{m.onboarding_resume_title()}</h2>
      <p className="mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>
        {m.onboarding_resume_subtitle()}
      </p>
      <p className="mt-0.5 text-xs" style={{ color: "var(--text-muted)" }}>{m.onboarding_resume_time_hint()}</p>

      {resumeCreated ? (
        <div className="mt-6 card card-mint p-4 text-center">
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full" style={{ backgroundColor: "var(--success-subtle)" }}>
            <svg className="h-5 w-5" style={{ color: "var(--success)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="mt-3 text-sm font-medium" style={{ color: "var(--text)" }}>
            {uploadedName ? m.onboarding_resume_created_with_name({ name: uploadedName }) : m.onboarding_resume_created()}
          </p>
          {parsedResumeData && (
            <div className="mt-4 animate-fade-up" style={{ animationDelay: "300ms" }}>
              <OnboardingAtsScore resumeData={parsedResumeData} />
            </div>
          )}
          <button onClick={onNext} className="btn btn-accent mt-4 w-full">
            {m.common_continue()}
          </button>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div
                className="flex h-9 w-9 items-center justify-center rounded-[10px]"
                style={{ backgroundColor: "var(--surface-raised)" }}
              >
                <svg className="h-4.5 w-4.5" style={{ color: "var(--text-muted)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium" style={{ color: "var(--text)" }}>{m.onboarding_resume_upload_title()}</p>
                <p className="text-xs" style={{ color: "var(--text-muted)" }}>{m.onboarding_resume_upload_format()}</p>
              </div>
            </div>
            <label className="mt-3 flex cursor-pointer items-center justify-center rounded-xl border-2 border-dashed p-6 text-sm transition-colors hover:border-[var(--text-muted)]" style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}>
              {isUploading ? (
                <span className="flex items-center gap-2">
                  <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                  {m.onboarding_resume_upload_processing()}
                </span>
              ) : (
                m.onboarding_resume_upload_cta()
              )}
              <input
                type="file"
                accept=".pdf,.docx"
                onChange={onUpload}
                disabled={isUploading}
                className="sr-only"
              />
            </label>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t" style={{ borderColor: "var(--border)" }} />
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 text-xs" style={{ backgroundColor: "var(--surface)", color: "var(--text-muted)" }}>{m.common_or()}</span>
            </div>
          </div>

          <button
            onClick={onBuildFromScratch}
            disabled={isUploading}
            className="btn btn-secondary w-full"
          >
            {m.onboarding_resume_build_scratch()}
          </button>

          {uploadError && (
            <div className="rounded-lg border p-3" style={{ borderColor: "var(--error)", background: "var(--error-subtle)" }}>
              <p className="text-sm font-medium" style={{ color: "var(--error)" }}>{m.onboarding_upload_error_parse()}</p>
              <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{m.onboarding_upload_error_parse_hint()}</p>
            </div>
          )}
        </div>
      )}

      <div className="mt-6 flex items-center justify-between">
        <button onClick={onBack} className="btn btn-ghost btn-sm">
          {m.common_back()}
        </button>
        {!resumeCreated && (
          <button onClick={onSkip} className="btn btn-ghost btn-sm">
            {m.common_skip_for_now()}
          </button>
        )}
      </div>
    </div>
  );
}

function StepComplete({
  uploadedName, onComplete, isCompleting, onBack,
}: {
  uploadedName: string | null;
  onComplete: () => void;
  isCompleting: boolean;
  onBack: () => void;
}) {
  return (
    <div className="text-center">
      <div
        className="mx-auto flex h-14 w-14 items-center justify-center rounded-[13px]"
        style={{ backgroundColor: "var(--success-subtle)" }}
      >
        <svg className="h-7 w-7" style={{ color: "var(--success)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h2 className="mt-5 text-xl font-bold" style={{ color: "var(--text)" }}>{m.onboarding_complete_title()}</h2>
      <p className="mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>
        {m.onboarding_complete_subtitle()}
      </p>

      <div className="mt-6 space-y-3 text-left">
        <div className="card p-3 flex items-center gap-3">
          <div
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[10px]"
            style={{ backgroundColor: uploadedName ? "var(--success-subtle)" : "var(--surface-raised)" }}
          >
            {uploadedName ? (
              <svg className="h-4 w-4" style={{ color: "var(--success)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
            ) : (
              <svg className="h-4 w-4" style={{ color: "var(--text-muted)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
            )}
          </div>
          <div>
            <p className="text-sm font-medium" style={{ color: "var(--text)" }}>{m.onboarding_complete_resume()}</p>
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              {uploadedName ? m.onboarding_complete_resume_uploaded({ name: uploadedName }) : m.onboarding_complete_resume_pending()}
            </p>
          </div>
          <span className={`badge ml-auto ${uploadedName ? "badge-success" : "badge-default"}`}>
            {uploadedName ? m.common_done() : m.common_pending()}
          </span>
        </div>

        <div className="card p-3 flex items-center gap-3">
          <div
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[10px]"
            style={{ backgroundColor: "var(--success-subtle)" }}
          >
            <svg className="h-4 w-4" style={{ color: "var(--success)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
          </div>
          <div>
            <p className="text-sm font-medium" style={{ color: "var(--text)" }}>{m.onboarding_complete_ai_engine()}</p>
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              {m.onboarding_complete_ai_desc()}
            </p>
          </div>
          <span className="badge ml-auto badge-success">{m.common_ready()}</span>
        </div>
      </div>

      <button
        onClick={onComplete}
        disabled={isCompleting}
        className="btn btn-accent mt-8 w-full"
      >
        {isCompleting ? m.onboarding_complete_finishing() : m.onboarding_complete_cta()}
      </button>

      <button onClick={onBack} className="btn btn-ghost btn-sm mt-3">
        {m.common_back()}
      </button>
    </div>
  );
}
